import dayjs from 'dayjs';
import { error, warning, neutral, success } from '_lex/_foundations/phxColors';
import { MetricStatus, TimeRangeFilter } from '../_types';

// Status thresholds for metric cards
export const STATUS_THRESHOLDS = {
  GOOD: 80,
  CONCERNING: 60,
} as const;

/** For percent-based metrics (ACKs, DTBs, DTB Assignments Created) — higher is better */
export const getMetricStatus = (percent: number): MetricStatus => {
  if (percent >= STATUS_THRESHOLDS.GOOD) return 'GOOD';
  if (percent >= STATUS_THRESHOLDS.CONCERNING) return 'CONCERNING';
  return 'CRITICAL';
};

/** For pending policy updates count — lower is better */
export const getPendingUpdatesStatus = (count: number): MetricStatus => {
  if (count < 5) return 'GOOD';
  if (count <= 20) return 'CONCERNING';
  return 'CRITICAL';
};

// Status colors mapping
export const STATUS_COLORS = {
  GOOD: neutral[300],
  CONCERNING: warning[300],
  CRITICAL: error[800],
} as const;

// Chart colors for different data series
export const CHART_COLORS = {
  // Policy updates
  pendingUpdates: error[500],
  processed: success[600],
  
  // Acknowledgments/DTBs completed
  onTime: success[600],
  overdueByLessThanWeek: error[500],
  overdueByWeekOrMore: error[800],
  
  // DTB completion
  completedOnTime: success[600],
  completedLate: error[500],
  
  // Lexipol updates
  accepted: '#B6E0FF', // primary[200]
  modified: '#76C8FF', // primary[300]
  rejected: '#004B87', // primary[800]
  lexipolPending: '#CDD5DF', // neutral[300]
  
  // Assignments
  completedLine: success[600],
  overdueLine: error[700],
  completedArea: success[200],
  overdueArea: error[200],
} as const;

// Time range filter options with labels
export const TIME_RANGE_OPTIONS = [
  { value: TimeRangeFilter.PREVIOUS_MONTH, label: 'Previous Month' },
  { value: TimeRangeFilter.PAST_3_MONTHS, label: 'Past 3 months' },
  { value: TimeRangeFilter.YEAR_TO_DATE, label: 'Year-to-date' },
  { value: TimeRangeFilter.PAST_12_MONTHS, label: 'Past 12 months' },
  { value: TimeRangeFilter.LAST_CALENDAR_YEAR, label: 'Last Calendar Year' },
  { value: TimeRangeFilter.MONTH_TO_DATE, label: 'Month-to-date' },
  { value: TimeRangeFilter.PAST_30_DAYS, label: 'Past 30 days' },
  { value: TimeRangeFilter.PAST_2_YEARS, label: 'Past 2 years' },
  { value: TimeRangeFilter.PAST_3_YEARS, label: 'Past 3 years' },
] as const;

// All ranges are whole months and exclude the current month (except PAST_30_DAYS and MONTH_TO_DATE).
// e.g. On Feb 9, 2026: PAST_3_MONTHS => startDate 2025-11-01, endDate 2026-01-31.
export const getDateRangeFromFilter = (filter: TimeRangeFilter): { startDate: string; endDate: string } => {
  const today = dayjs();
  const lastDayPrevMonth = today.subtract(1, 'month').endOf('month');

  let startDate: dayjs.Dayjs;
  let endDate: dayjs.Dayjs;

  switch (filter) {
    case TimeRangeFilter.PREVIOUS_MONTH:
      startDate = today.subtract(1, 'month').startOf('month');
      endDate = lastDayPrevMonth;
      break;
    case TimeRangeFilter.PAST_30_DAYS:
      startDate = today.subtract(30, 'day');
      endDate = today;
      break;
    case TimeRangeFilter.PAST_3_MONTHS:
      startDate = today.subtract(3, 'month').startOf('month');
      endDate = lastDayPrevMonth;
      break;
    case TimeRangeFilter.YEAR_TO_DATE:
      startDate = today.startOf('year');
      endDate = lastDayPrevMonth;
      break;
    case TimeRangeFilter.PAST_12_MONTHS:
      startDate = today.subtract(12, 'month').startOf('month');
      endDate = lastDayPrevMonth;
      break;
    case TimeRangeFilter.LAST_CALENDAR_YEAR:
      startDate = today.subtract(1, 'year').startOf('year');
      endDate = today.subtract(1, 'year').endOf('year');
      break;
    case TimeRangeFilter.MONTH_TO_DATE:
      startDate = today.startOf('month');
      endDate = today;
      break;
    case TimeRangeFilter.PAST_2_YEARS:
      startDate = today.subtract(2, 'year').startOf('month'); // 24 full months through last month
      endDate = lastDayPrevMonth;
      break;
    case TimeRangeFilter.PAST_3_YEARS:
      startDate = today.subtract(3, 'year').startOf('month'); // 36 full months through last month
      endDate = lastDayPrevMonth;
      break;
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  };
};

// Mock manual types
export const MOCK_MANUALS = [
  { id: 'all', name: 'All' },
  { id: 'policy', name: 'Policy Manual' },
  { id: 'fire', name: 'Fire Manual' },
  { id: 'ems', name: 'EMS Manual' },
] as const;

